import {
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { SearchIcon, ViewGridIcon, Density1Icon } from '../../utils/icons';
import { TestIds } from '../../constants/testIds';

interface ProductsToolbarProps {
  readonly query: string;
  readonly onQueryChange: (q: string) => void;
  readonly sortMode: 'az' | 'pinned';
  readonly onSortChange: (mode: 'az' | 'pinned') => void;
  readonly viewMode: 'grid' | 'table';
  readonly onViewModeChange: (mode: 'grid' | 'table') => void;
}

export function ProductsToolbar({
  query,
  onQueryChange,
  sortMode,
  onSortChange,
  viewMode,
  onViewModeChange,
}: ProductsToolbarProps) {
  return (
    <Stack
      direction={{ md: 'row' }}
      alignItems={{ xs: 'flex-start', md: 'center' }}
      justifyContent="space-between"
      gap={2}
    >
      <TextField
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder="Search products"
        sx={{ width: '100%', flex: { md: 1 }, maxWidth: { md: 600 } }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
          htmlInput: {
            'aria-label': 'Search products',
            'data-testid': TestIds.ALL_PRODUCTS_SEARCH_INPUT,
          },
        }}
      />
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography
          variant="bodyXSmall"
          color="text.secondary"
          textTransform="uppercase"
          fontWeight={600}
        >
          Sort by
        </Typography>
        <ToggleButtonGroup
          value={sortMode}
          exclusive
          onChange={(_, value: 'az' | 'pinned' | null) => {
            if (value !== null) onSortChange(value);
          }}
          aria-label="Sort products"
        >
          <ToggleButton
            value="az"
            aria-label="Sort A to Z"
            data-testid={TestIds.ALL_PRODUCTS_SORT_AZ}
          >
            A–Z
          </ToggleButton>
          <ToggleButton
            value="pinned"
            aria-label="Sort by pinned"
            data-testid={TestIds.ALL_PRODUCTS_SORT_PINNED}
          >
            Pinned
          </ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, value: 'grid' | 'table' | null) => {
            if (value !== null) onViewModeChange(value);
          }}
          aria-label="View mode"
        >
          <ToggleButton
            value="table"
            aria-label="Table view"
            data-testid={TestIds.ALL_PRODUCTS_VIEW_TABLE}
            sx={{ gap: 1 }}
          >
            <Density1Icon fontSize="small" />
            Table
          </ToggleButton>
          <ToggleButton
            value="grid"
            aria-label="Grid view"
            data-testid={TestIds.ALL_PRODUCTS_VIEW_GRID}
            sx={{ gap: 1 }}
          >
            <ViewGridIcon fontSize="small" />
            Grid
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </Stack>
  );
}
